// Tenant create form. The "happy" path with X-Vault-Sync: error is
// still a SUCCESS — useMutation.onSuccess inspects the envelope's
// vaultSyncFailed flag and either dismisses the modal (true sync OK)
// or keeps it open with the VaultSyncBanner so the operator
// acknowledges before navigating away.

import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { tenants } from "../../api/endpoints/tenants";
import { qk } from "../../api/queryKeys";
import { FormShell } from "../../components/forms/FormShell";
import { TextField } from "../../components/forms/TextField";
import { slugify } from "../../lib/slug";
import type { TenantCreate, TenantOut } from "../../api/types/tenant";

import { VaultSyncBanner } from "./VaultSyncBanner";

interface TenantCreateFormProps {
  accountSlug: string;
  onDone: () => void;
  // Lets the parent (e.g. Modal) lock its close affordances while
  // the create POST is in flight, so the operator can't bail out
  // before the degraded-success banner surfaces.
  onPendingChange?: (pending: boolean) => void;
}

const SLUG_RE = /^[a-z0-9][a-z0-9-]{0,62}$/;

interface FormState {
  slug: string;
  display_name: string;
  support_email: string;
}

const EMPTY: FormState = {
  slug: "",
  display_name: "",
  support_email: "",
};

export function TenantCreateForm({
  accountSlug,
  onDone,
  onPendingChange,
}: TenantCreateFormProps) {
  const queryClient = useQueryClient();
  const [values, setValues] = useState<FormState>(EMPTY);
  const [degraded, setDegraded] = useState<TenantOut | null>(null);
  // Auto-suggest slug from display_name until the operator edits it.
  // See lib/slug.ts + AccountCreateForm for the established pattern.
  const slugTouched = useRef(false);

  const slugError = useMemo(() => {
    if (!values.slug) return null;
    return SLUG_RE.test(values.slug)
      ? null
      : "Slug must match a-z, 0-9, hyphen; 1–63 chars; cannot start with hyphen.";
  }, [values.slug]);

  const mutation = useMutation({
    mutationFn: async () => {
      const body: TenantCreate = {
        slug: values.slug,
        display_name: values.display_name,
        support_email: values.support_email || null,
      };
      return tenants.create(accountSlug, body);
    },
    onSuccess: (resp) => {
      // List + detail caches: either way the row exists, invalidate.
      queryClient.invalidateQueries({ queryKey: qk.tenants(accountSlug) });
      queryClient.invalidateQueries({
        queryKey: qk.tenant(accountSlug, resp.data.slug),
      });

      if (resp.vaultSyncFailed) {
        // 201 with X-Vault-Sync: error — keep the modal open so the
        // operator sees the warning, then offers a manual "Go to
        // tenant detail" link from VaultSyncBanner.
        setDegraded(resp.data);
        return;
      }
      slugTouched.current = false; // next tenant starts fresh with auto-suggest
      onDone();
    },
  });

  const isValid =
    values.slug !== "" &&
    SLUG_RE.test(values.slug) &&
    values.display_name !== "";

  // Mirror mutation pending state up to the parent (Modal closes
  // disabled while in flight).
  useEffect(() => {
    onPendingChange?.(mutation.isPending);
  }, [mutation.isPending, onPendingChange]);

  if (degraded) {
    return (
      <>
        <VaultSyncBanner tenant={degraded} />
        <footer style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
          <button
            type="button"
            onClick={() => {
              setDegraded(null);
              setValues(EMPTY);
              onDone();
            }}
            style={primaryBtn}
          >
            Done
          </button>
        </footer>
      </>
    );
  }

  return (
    <FormShell
      submitLabel="Create tenant"
      onSubmit={(e) => {
        e.preventDefault();
        if (!isValid) return;
        mutation.mutate();
      }}
      onCancel={onDone}
      pending={mutation.isPending}
      error={mutation.error}
      errorTitle="Create tenant failed"
    >
      <TextField
        label="Display name"
        value={values.display_name}
        onChange={(display_name) =>
          setValues((v) => ({
            ...v,
            display_name,
            slug: slugTouched.current ? v.slug : slugify(display_name),
          }))
        }
        required
        placeholder="Khawaja Home"
        helper="The human-readable name. Slug below is auto-derived; edit it if you want something shorter."
      />
      <TextField
        label="Slug"
        value={values.slug}
        onChange={(slug) => {
          slugTouched.current = true;
          setValues((v) => ({ ...v, slug }));
        }}
        required
        placeholder="khawaja-home"
        helper="Globally unique. Auto-derived from display name until you edit it. There is no rename — typos stick."
        error={slugError}
      />
      <TextField
        label="Support email"
        type="email"
        value={values.support_email}
        onChange={(support_email) => setValues((v) => ({ ...v, support_email }))}
        placeholder="support@orixen.ai"
        helper="Optional. Falls back to support@orixen.ai on the Vault side."
      />
      <p style={haUrlHint}>
        HA URL is set on the HA instance create form, which appears
        under this tenant once it's created.
      </p>
    </FormShell>
  );
}

const haUrlHint: React.CSSProperties = {
  color: "var(--text-dim)",
  fontSize: "0.85rem",
  marginTop: 8,
  marginBottom: 0,
};

const primaryBtn: React.CSSProperties = {
  background: "var(--accent)",
  color: "var(--bg)",
  border: "none",
  padding: "8px 16px",
  borderRadius: 4,
  fontSize: "0.9rem",
  fontWeight: 600,
  cursor: "pointer",
};
